import React from 'react';

import { fmt } from '../ContractStatistics.Functions';
import * as Functions from './BillingControl.Functions';

interface IProps {
    funnel: Functions.BillingFunnel;
    currency: string;
}

/**
 * El circuito del dinero del contrato, escalón por escalón.
 *
 * Se lee de izquierda a derecha: lo que se pactó, lo que se ejecutó, lo que alguien
 * cargó, lo que quedó valorizado, lo que entró en una liquidación y lo que se cobró.
 * Debajo de cada escalón, en rojo, lo que se cayó respecto del anterior: ahí está la
 * plata que hay que ir a buscar.
 */
export const BillingFunnelComponent: React.FC<IProps> = ({ funnel, currency }) => (
    <div className="cs-card">
        <div className="cs-card__header">
            <div className="cs-card__title">El circuito del dinero</div>
            <div className="cs-card__desc">
                De lo pactado a lo cobrado. Cada escalón contiene al siguiente: lo cobrado también pasó por una
                liquidación. Lo que baja de un escalón al otro es lo que quedó en el camino.
            </div>
        </div>

        <div className="bc-funnel">
            {funnel.steps.map((step) => (
                <div key={step.key} className={'bc-step' + (step.key === 'collected' ? ' bc-step--collected' : '')}>
                    <span className="bc-step__label">{step.label}</span>
                    <span className="bc-step__value">
                        {step.amount !== undefined ? fmt(step.amount, currency) : step.count}
                    </span>
                    {step.amount !== undefined && (
                        <span className="bc-step__count">
                            {step.count} {step.count === 1 ? 'consumo' : 'consumos'}
                        </span>
                    )}
                    <span className="bc-step__desc">{step.description}</span>
                    {!!step.dropCount && step.dropCount > 0 && (
                        <span className="bc-step__drop">
                            −{step.dropCount} {step.dropAmount ? `· ${fmt(step.dropAmount, currency)}` : ''}
                        </span>
                    )}
                </div>
            ))}
        </div>

        <div className="cs-card__body">
            <div className="cs-table-wrap">
                <table className="cs-table">
                    <thead>
                        <tr>
                            <th>Estado de la plata cargada</th>
                            <th className="cs-table__amount">Importe</th>
                            <th className="cs-table__amount">% del total</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><strong>Sin liquidar</strong> — todavía no entró en ninguna liquidación</td>
                            <td className="cs-table__amount">{fmt(funnel.totals.unsettled, currency)}</td>
                            <td className="cs-table__amount">
                                {funnel.totals.total > 0
                                    ? `${Math.round((funnel.totals.unsettled / funnel.totals.total) * 100)}%`
                                    : '—'}
                            </td>
                        </tr>
                        <tr>
                            <td>En liquidación, pendiente de cobro</td>
                            <td className="cs-table__amount">{fmt(funnel.totals.inSettlement, currency)}</td>
                            <td className="cs-table__amount">
                                {funnel.totals.total > 0
                                    ? `${Math.round((funnel.totals.inSettlement / funnel.totals.total) * 100)}%`
                                    : '—'}
                            </td>
                        </tr>
                        <tr>
                            <td>Cobrado</td>
                            <td className="cs-table__amount">{fmt(funnel.totals.collected, currency)}</td>
                            <td className="cs-table__amount">
                                {funnel.totals.total > 0
                                    ? `${Math.round((funnel.totals.collected / funnel.totals.total) * 100)}%`
                                    : '—'}
                            </td>
                        </tr>
                        <tr>
                            <td>Fuera de liquidación — rechazados o ignorados</td>
                            <td className="cs-table__amount">{fmt(funnel.totals.excluded, currency)}</td>
                            <td className="cs-table__amount">
                                {funnel.totals.total > 0
                                    ? `${Math.round((funnel.totals.excluded / funnel.totals.total) * 100)}%`
                                    : '—'}
                            </td>
                        </tr>
                        <tr className="fp-total">
                            <td>Total cargado</td>
                            <td className="cs-table__amount">{fmt(funnel.totals.total, currency)}</td>
                            <td className="cs-table__amount">100%</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
);

export default BillingFunnelComponent;
